import { CalendarCheck, Pin } from 'lucide-react';
import { ChatChannel } from '@/api/chat';
import { cn } from '@/lib/utils';
import { useFeatures, Feature } from '@/contexts/FeaturesContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface PinnedChannelItemProps {
  channel: ChatChannel;
  isSelected: boolean;
  onClick: () => void;
}

export function PinnedChannelItem({ channel, isSelected, onClick }: PinnedChannelItemProps) {
  const confirmedSessions = channel.confirmedSessions || [];
  const sessionCount = confirmedSessions.length;
  
  // Use the first session's description for preview, or legacy field
  const primaryTimeDescription = confirmedSessions[0]?.timeDescription || channel.sessionTimeDescription;
  
  const { isEnabled } = useFeatures();
  const showProfilePictures = isEnabled(Feature.PROFILE_PICTURE.name);

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full p-2.5 hover:bg-accent rounded-lg transition-colors text-left',
        isSelected && 'bg-accent'
      )}
    >
      <div className="flex items-center gap-2.5">
        {/* Channel Icon / Avatar */}
        {channel.type === 'PRIVATE' && showProfilePictures && channel.directMessageAvatarUrl ? (
          <Avatar className="h-6 w-6 flex-shrink-0">
            <AvatarImage src={`/api/v1/files/${channel.directMessageAvatarUrl}`} alt={channel.name || 'Direct message'} />
            <AvatarFallback>
              {(channel.name || 'D').split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ) : (
          <Pin className="h-4 w-4 text-burgundy-600 flex-shrink-0" />
        )}
        
        <div className="flex-1 min-w-0">
          {/* Name */}
          <div className="font-medium truncate text-foreground">
            {channel.name || 'Chat'}
          </div>
          
          {/* Session Info */}
          <div className="flex items-center gap-1 text-xs text-burgundy-600">
            {primaryTimeDescription && (
              <span className="truncate">{primaryTimeDescription}</span>
            )}
            {sessionCount > 1 && (
              <span className="flex items-center gap-0.5 text-burgundy-500 flex-shrink-0">
                <CalendarCheck className="h-3 w-3" />
                +{sessionCount - 1} more
              </span>
            )}
          </div>
        </div>
        
        {/* Unread indicator */}
        {channel.hasUnread && channel.unreadCount && channel.unreadCount > 0 && (
          <div className="h-2 w-2 rounded-full bg-burgundy-600 flex-shrink-0" />
        )}
      </div>
    </button>
  );
}
